import React, { useEffect, useRef } from 'react';
import {useAppSelector} from '../../store/hooks';
import {Category, Question} from '../../types/questions';
import questions from '../../data/questions';
import PollFinish from './PollFinish';
import {
  CTA,
  Progress,
  styled,
  Text,
  TextTag,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import useTrans from '../../hooks/use-trans';
import { CTAWrapper } from '../../styles/global.style';
import { useFeatureServices } from '../../use-feature-services';
import { Language } from '../../types/locale';
import { Questionnaire, QuestionnaireContentItem } from '../../types/tracking';
// import { Language } from '../../types/locale';

const PollProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.size.dynamic0100};
  padding: 0 ${(props) => props.theme.size.grid001};
`;

const PollActionContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.size.dynamic0100};
  @media screen and (min-width: 480px) {
    flex-direction: row;
  }
`;

const StepContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ProgressContainer = styled.div``;

interface PollProgressInterface {
  category: Category;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
}

const PollProgress: React.FC<PollProgressInterface> = ({
  category,
  currentStep,
  totalSteps,
  onNext,
  onBack,
}) => {

  const { tracking } = useFeatureServices();

  const trans = useTrans();
  const enTrans = useTrans("en" as Language);

  const answers = useAppSelector((state) => state.answers);

  const progressValue = (currentStep + 1) / totalSteps;

  const pendingTrack = useRef<'back' | 'next' | null>(null);

  const buildQuestionnaire = (): Questionnaire => {
    const categories = Object.entries(questions) as [Category, Question[]][];
    const content: QuestionnaireContentItem[] = categories
      .filter(([cat]) => cat === category || answers[cat] !== null)
      .map(([cat]) => {
        const selected = answers[cat];
        const catTrans = trans.questions[cat as keyof typeof trans.questions];
        const rawValue = catTrans.polls[selected?.key as keyof typeof catTrans.polls];
        const cleanValue = rawValue?.replace(/^["'“”]|["'“”]$/g, '') || rawValue;
        return {
          question: catTrans.title,
          id: cat,
          answer: selected
            ? [{ type: 'string', value: cleanValue }]
            : [],
          Active: cat === category,
        };
      });
    return {
      QuestionAnswerSet: {
        id: 'SUVW Questionnaire',
        version: '1',
        content,
      },
    };
  };

  useEffect(() => {
    if (pendingTrack.current === null) return;

    const catTrans = trans.questions[category as keyof typeof trans.questions];

    tracking.track('VWBasic_Step_Pageload', {
      eventInfo: {
        pageCategory: 'Poll',
        brand: "passenger"
      },
      process: {
        Name: 'SUVW Questionnaire and Lead Capture Form',
        StepName: `Questionnaire - ${catTrans.title}`,
        StepNumber: `${currentStep + 1}`,
      },
    });

    pendingTrack.current = null;
  }, [currentStep]);

  const handleBackClick = () => {
    pendingTrack.current = 'back';

    const catTrans = trans.questions[category as keyof typeof trans.questions];

    tracking.track('VWBasic_Button_Click', {
      eventInfo: {
        contentId: `Back button - Question ${currentStep}`,
        brand: "passenger"
      },
      process: {
        Name: 'SUVW Questionnaire and Lead Capture Form',
        StepName: `Questionnaire - ${catTrans.title}`,
        StepNumber: `${currentStep + 1}`,
      },
      link: {
        url: window.location.href,
        name: enTrans.pages.poll.back,
      },
    });

    onBack();
  };

  const handleNextClick = () => {
    pendingTrack.current = 'next';

    const catTrans = trans.questions[category as keyof typeof trans.questions];

    tracking.track('VWBasic_Button_Click', {
      eventInfo: {
        contentId: `Next button - Question ${currentStep + 2}`,
        brand: "passenger"
      },
      process: {
        Name: 'SUVW Questionnaire and Lead Capture Form',
        StepName: `Questionnaire - ${catTrans.title}`,
        StepNumber: `${currentStep + 1}`,
      },
      link: {
        url: window.location.href,
        name: enTrans.pages.poll.next,
      },
      questionnaire: buildQuestionnaire(),
    });

    onNext();
  };

  return (
    <PollProgressContainer>
      <PollActionContainer>
        {currentStep ? (
          <CTAWrapper>
            <CTA
              tag="button"
              emphasis="secondary"
              onClick={handleBackClick}
              aria-label={trans.pages.poll.back}
              type="button"
            >
              {trans.pages.poll.back}
            </CTA>
          </CTAWrapper>
        ) : null}

        {currentStep < totalSteps - 1 ? (
          <CTAWrapper>
            <CTA
              disabled={answers[category] == null}
              tag="button"
              emphasis="primary"
              onClick={handleNextClick}
              aria-label={trans.pages.poll.next}
              type="button"
            >
              {trans.pages.poll.next}
            </CTA>
          </CTAWrapper>
        ) : (
          <PollFinish disabled={answers[category] == null} />
        )}
      </PollActionContainer>
      <ProgressContainer>
        <Progress value={progressValue} />
      </ProgressContainer>
      <StepContainer>
        <Text appearance={TokenTextAppearance.copy100} bold tag={TextTag.span}>
          {currentStep + 1}/{totalSteps}
        </Text>
      </StepContainer>
    </PollProgressContainer>
  );
};

export default PollProgress;
